import React, { Fragment, useEffect, useState } from 'react';
import { Table, Button, DatePicker, message, Input, Select } from 'antd';
import { Col, Row } from 'react-bootstrap';
import Card from '../../../components/bootstrap/card';
import { getRodtepDataService } from '../../../services/RodtepReportService';
import { getReportSummaryData, getAllAdminIECsService } from '../../../services/FileDataService';
import { getTodaysDate, formatToTwoDecimals } from '../../../utilities/utils';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import { getBRCorelationService } from '../../../services/MasterService';
import { useLocation } from 'react-router-dom';
import ReportDateFilter from '../../../components/ReportDateFilter';

import useColumnVisibility from '../../../hooks/useColumnVisibility';
import ColumnSelector from '../../../components/common/ColumnSelector';

const { RangePicker } = DatePicker;

const RodtepReport = ({ data }) => {
  const location = useLocation();
  const [IECOptions, setIECOptions] = useState([]);
  const userRole = localStorage.getItem('role');
  const [selectedIEC, setSelectedIEC] = useState(localStorage.getItem('iec_no'));
  const [jsonData, setJsonData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoading2, setIsLoading2] = useState(false);
  const [summaryDashboardData, setSummaryDashboardData] = useState([]);
  const [selectedIECName, setSelectedIECName] = useState(localStorage.getItem('name'));
  const [isValid, setIsValid] = useState(true);
  const [billNo, setBillNo] = useState('');
  const [branchCodeNames, setBranchCodeNames] = useState([]);
  const [selectedBranchCodes, setSelectedBranchCodes] = useState([]);

  const currentYear = dayjs().year();
  const defaultFrom = dayjs(`${currentYear}-01-01`);
  const defaultTo = dayjs(`${currentYear}-12-31`);

  const [dates, setDates] = useState(() => {
    if (location.state?.passedDates?.length === 2) {
      return [
        dayjs(location.state.passedDates[0]),
        dayjs(location.state.passedDates[1]),
      ];
    }
    return [defaultFrom, defaultTo];
  });

  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const year = today.getFullYear();
  const formattedDate = `${day}-${month}-${year}`;

  // ─── TABLE STYLES ────────────────────────────────────────────────────────────
  const tableStyles = `
    .rodtep-table .ant-table-thead > tr > th,
    .rodtep-summary-table .ant-table-thead > tr > th {
      white-space: normal !important;
      word-break: break-word !important;
      overflow: visible !important;
      text-overflow: unset !important;
      vertical-align: middle !important;
      line-height: 1.3 !important;
      padding-top: 8px !important;
      padding-bottom: 8px !important;
    }
    .rodtep-table .ant-table-tbody > tr > td,
    .rodtep-summary-table .ant-table-tbody > tr > td {
      white-space: nowrap;
    }
    .rodtep-table .ant-table-container,
    .rodtep-summary-table .ant-table-container {
      overflow-x: auto !important;
    }
    .rodtep-table .ant-table-header,
    .rodtep-summary-table .ant-table-header {
      overflow: hidden !important;
    }
    .rodtep-table .ant-table-sticky-holder,
    .rodtep-summary-table .ant-table-sticky-holder {
      overflow: hidden !important;
    }
  `;

  function formatDateToDDMMYY(dateString) {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = String(date.getFullYear()).slice(-2);
    return `${d}/${m}/${y}`;
  }

  const getDateOnly = (dateString) => {
    if (dateString && dateString.length !== 0) {
      return new Date(dateString);
    }
    return '';
  };

  const columns = [
    {
      title: 'BUSINESS UNIT',
      dataIndex: 'business_unit',
      key: 'business_unit',
      width: 160,
      fixed: 'left'
    },
    {
      title: 'PLANT NAME',
      dataIndex: 'plant_name',
      key: 'plant_name',
      width: 150,
      fixed: 'left'
    },
    {
      title: 'IEC/BR CODE',
      dataIndex: 'iec_br',
      key: 'iec_br',
      width: 150,
    },
    {
      title: 'PORT',
      dataIndex: 'port_code',
      key: 'port_code',
      width: 90,
    },
    {
      title: 'FY',
      dataIndex: 'fy',
      key: 'fy',
      width: 80,
    },
    {
      title: 'SHB NO.',
      dataIndex: 'sb_no',
      key: 'sb_no',
      width: 115,
    },
    {
      title: 'SHB DT.',
      dataIndex: 'sb_date',
      key: 'sb_date',
      width: 110,
    },
    {
      title: 'LEO DT.',
      dataIndex: 'leo_date',
      key: 'leo_date',
      width: 110,
      render: (text, record) => formatDateToDDMMYY(record.leo_date),
    },
    {
      title: 'RODTEP AMT.',
      dataIndex: 'rodtep_amt',
      key: 'rodtep_amt',
      width: 130,
      align: 'right',
      render: (text) => formatToTwoDecimals(text),
    },
    {
      title: 'SCROLL NO. / QUERY',
      dataIndex: 'rodtep_scroll_number',
      key: 'rodtep_scroll_number',
      width: 170,
    },
    {
      title: 'SCROLL DT.',
      dataIndex: 'rodtep_scroll_date',
      key: 'rodtep_scroll_date',
      width: 110,
      render: (text, record) =>
        record.rodtep_scroll_date ? formatDateToDDMMYY(record.rodtep_scroll_date) : '',
    },
    {
      title: 'SCROLL AMT.',
      dataIndex: 'rodtep_scroll_amount',
      key: 'rodtep_scroll_amount',
      width: 120,
      align: 'right',
      render: (text) => formatToTwoDecimals(text),
    },
    {
      title: 'BRC NO.',
      dataIndex: 'brc_no',
      key: 'brc_no',
      width: 120,
    },
    {
      title: 'DATE OF REALISATION',
      dataIndex: 'date_of_realization',
      key: 'date_of_realization',
      width: 170,
      render: (text, record) =>
        record.date_of_realization ? formatDateToDDMMYY(record.date_of_realization) : '',
    },
    {
      title: 'PORT/FCC DIFF.',
      dataIndex: 'port_currency_difference',
      key: 'port_currency_difference',
      width: 130,
    },
    {
      title: 'VALUE DIFF.',
      dataIndex: 'value_difference',
      key: 'value_difference',
      width: 120,
      align: 'right',
      render: (text) => formatToTwoDecimals(text),
    },
    {
      title: 'FCC RELEASED IN (DAYS)',
      dataIndex: 'payment_released_in_days',
      key: 'payment_released_in_days',
      width: 180,
      align: 'right',
    },
    {
      title: `RODTEP TOBE SURRENDER (${formatDateToDDMMYY(new Date())})`,
      dataIndex: 'liability',
      key: 'liability',
      width: 240,
      align: 'right',
      render: (text) => formatToTwoDecimals(text),
    },
    {
      title: 'RODTEP SCRIP NO.',
      dataIndex: 'rodtep_scrip_number',
      key: 'rodtep_scrip_number',
      width: 160,
    },
    {
      title: 'RODTEP SCRIP DT.',
      dataIndex: 'rodtep_scrip_issue_date',
      key: 'rodtep_scrip_issue_date',
      width: 150,
      align: 'right',
      render: (text, record) =>
        record.rodtep_scrip_issue_date ? formatDateToDDMMYY(record.rodtep_scrip_issue_date) : '',
    },
    {
      title: 'RODTEP SCRIP AMT.',
      dataIndex: 'rodtep_scrip_sb_amount',
      key: 'rodtep_scrip_sb_amount',
      width: 160,
      align: 'right',
      render: (text) => formatToTwoDecimals(text),
    },
    {
      title: 'RODTEP SCRIP VALID TILL',
      dataIndex: 'rodtep_scrip_expiry_date',
      key: 'rodtep_scrip_expiry_date',
      width: 190,
      render: (text, record) =>
        record.rodtep_scrip_expiry_date ? formatDateToDDMMYY(record.rodtep_scrip_expiry_date) : '',
    },
    {
      title: 'RODTEP REMARK',
      dataIndex: 'rodtep_remark',
      key: 'rodtep_remark',
      width: 150,
    },
    {
      title: 'RODTEP SCROLL TAT',
      dataIndex: 'rodtep_scroll_tat',
      key: 'rodtep_scroll_tat',
      width: 160,
      align: 'right',
    },
    {
      title: 'RODTEP SCRIP TAT',
      dataIndex: 'rodtep_script_tat',
      key: 'rodtep_script_tat',
      width: 150,
      align: 'right',
    },
    {
      title: 'CHALLAN NO',
      dataIndex: 'challan_no',
      key: 'challan_no',
      width: 130,
    },
    {
      title: 'CHALLAN DT.',
      dataIndex: 'challan_date',
      key: 'challan_date',
      width: 110,
    },
    {
      title: 'CHALLAN AMT.',
      dataIndex: 'challan_amount',
      key: 'challan_amount',
      width: 130,
      align: 'right',
    },
    {
      title: 'LIABILITY PAID AMT.',
      dataIndex: 'liability_amount_paid',
      key: 'liability_amount_paid',
      width: 160,
      align: 'right',
    },
    {
      title: 'PAID REMARK',
      dataIndex: 'remark',
      key: 'remark',
      width: 150,
    },
    {
      title: 'EGM No',
      dataIndex: 'egm_no',
      key: 'egm_no',
      width: 130,
    },
    {
      title: 'EGM Dtd.',
      dataIndex: 'egm_date',
      key: 'egm_date',
      width: 120,
      render: (text, record) =>
        record.egm_date ? formatDateToDDMMYY(record.egm_date) : '',
    },
    {
      title: 'EGM Remarks',
      dataIndex: 'egm_remarks',
      key: 'egm_remarks',
      width: 140,
    },
  ];

  const { visibleKeys, setVisibleKeys, filteredColumns, resetToDefault } =
    useColumnVisibility('RodtepReport', columns);

  const dashboardReportSummaryColumns = [
    {
      title: 'BUSINESS UNIT',
      dataIndex: 'business_unit',
      key: 'business_unit',
      width: 160,
      fixed: 'left'
    },
    {
      title: 'PLANT NAME',
      dataIndex: 'plant_name',
      key: 'plant_name',
      width: 150,
      fixed: 'left'
    },
    {
      title: 'FY',
      dataIndex: 'fy',
      key: 'fy',
      width: 80,
    },
    {
      title: 'IEC/BR CODE',
      dataIndex: 'iec_br',
      key: 'iec_br',
      width: 150,
    },
    {
      title: 'PORT CODE',
      dataIndex: 'port_code',
      key: 'port_code',
      width: 125,
    },
    {
      title: 'RODTEP REMARK',
      dataIndex: 'rodtep_remark',
      key: 'rodtep_remark',
      width: 160,
      render: (text, record) => (
        <span
          style={{ color: 'blue', cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => exportToExcel(text, record)}
        >
          {text}
        </span>
      ),
    },
    {
      title: 'RODTEP AMT.',
      dataIndex: 'rodtep_amt',
      key: 'rodtep_amt',
      width: 130,
      align: 'right',
      render: (text) => formatToTwoDecimals(text),
    },
    {
      title: 'SCROLL PENDING - AMT.',
      dataIndex: 'scroll_pending_amt',
      key: 'scroll_pending_amt',
      width: 180,
      align: 'right',
      render: (text) => formatToTwoDecimals(text),
    },
    {
      title: 'SCROLL GENERATED - AMT.',
      dataIndex: 'scroll_amt',
      key: 'scroll_amt',
      width: 190,
      align: 'right',
      render: (text) => formatToTwoDecimals(text),
    },
    {
      title: 'SCROLL AND SCRIP GENERATED AMT.',
      dataIndex: 'scroll_scrip_generated_amt',
      key: 'scroll_scrip_generated_amt',
      width: 230,
      align: 'right',
      render: (text) => formatToTwoDecimals(text),
    },
    {
      title: 'RODTEP LIABLITY AMT.',
      dataIndex: 'liability',
      key: 'liability',
      width: 170,
      align: 'right',
      render: (text) => formatToTwoDecimals(text),
    },
    {
      title: 'LIABILITY PAID AMT.',
      dataIndex: 'liability_amount_paid',
      key: 'liability_amount_paid',
      width: 160,
      align: 'right',
      render: (text) => formatToTwoDecimals(text),
    },
  ];

  const getSummaryDashboard = async () => {
    const filters = {
      from_date: dates?.[0] ? dayjs(dates[0]).format('YYYY-MM-DD') : null,
      to_date: dates?.[1] ? dayjs(dates[1]).format('YYYY-MM-DD') : null,
      bill_no: billNo.trim() || null,
      br_code: selectedBranchCodes || null,
    };
    try {
      setIsLoading2(true);
      const reportType = 'RODTEP-Exp';
      const response = await getReportSummaryData(reportType, filters, selectedIEC);
      if (response && response.data) {
        setSummaryDashboardData(response.data);
      }
      setIsLoading2(false);
    } catch (err) {
      setIsLoading2(false);
      console.log(err);
    }
  };

  const fetchData = async () => {
    const filters = {
      from_date: dates?.[0] ? dayjs(dates[0]).format('YYYY-MM-DD') : null,
      to_date: dates?.[1] ? dayjs(dates[1]).format('YYYY-MM-DD') : null,
      bill_no: billNo || null,
      br_code: selectedBranchCodes || null,
    };
    try {
      setIsLoading(true);
      const response = await getRodtepDataService(selectedIEC, filters);
      setJsonData(response);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error('Error fetching data:', error);
    }
  };

  // ─── useEffect 1: on mount ─────────────────────────────────────────────────
  // Guard added: only call getBRCorelationService if selectedIEC is not null
  // (prevents superadmin from firing a request with null IEC on first load)
  useEffect(() => {
    if (userRole === 'superadmin' || userRole === 'group_head') {
      getAdminIEC();
    }
    if (userRole === 'admin' || userRole === 'user') {
      fetchData();
      getSummaryDashboard();
    }
    if (selectedIEC) {
      getBRCorelationService(selectedIEC).then((response) => {
        setBranchCodeNames(response);
      });
    }
  }, []);

  // ─── useEffect 2: re-fetch branch codes when superadmin selects an IEC ────
  // This was missing — branch codes never updated after IEC selection
  useEffect(() => {
    if (selectedIEC) {
      getBRCorelationService(selectedIEC).then((response) => {
        setBranchCodeNames(response);
      });
      fetchData();
      getSummaryDashboard();
    }
  }, [selectedIEC]);

  const handleDateChange = (value) => {
    setDates(value || []);
    if (value && value.length === 2) {
      const from_date = value[0];
      const to_date = value[1];
      if (!from_date || !to_date) {
        setIsValid(false);
        return;
      }
      const oneYearLater = from_date.add(1, 'year');
      if (to_date.isAfter(oneYearLater)) {
        message.error('The selected duration should not exceed 1 year.');
        setIsValid(false);
        return;
      }
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  };

  const handleSubmit = () => {
    if (!selectedIEC) {
      message.error('Please select an IEC');
      return;
    }
    if (dates.length === 2 && isValid) {
      getSummaryDashboard();
      fetchData();
    } else {
      message.error('Please select a valid date range.');
    }
  };

  const getAdminIEC = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await getAllAdminIECsService(token);
      if (response?.data) {
        const options = response.data.map((item, index) => ({
          value: `${item.iec_no};${item.fullname}`,
          label: `${item.iec_no} (${item.fullname})`,
          key: index,
        }));
        setIECOptions(options);
      }
    } catch (err) {
      message.error('Failed to load IEC list');
    }
  };

  const handleIECSelection = (val) => {
    if (val) {
      const [iec_no, iec_name] = val.split(';');
      setSelectedIEC(iec_no);
      setSelectedIECName(iec_name);
    } else {
      setSelectedIEC(null);
      setSelectedIECName('');
    }
  };

  const handleBillSearch = (value) => {
    setBillNo(value);
  };

  const handleBranchCodeSelection = (values) => {
    setSelectedBranchCodes(values);
  };

  const exportToExcel = async (filterRemark = null, record = null) => {
    const workbook = new ExcelJS.Workbook();
    const summaryWorksheet = workbook.addWorksheet('Summary');
    const worksheet = workbook.addWorksheet('Data');

    let exportData = jsonData;
    let exportSummaryData = summaryDashboardData;

    if (record) {
      const ignoreKeys = [
        'rodtep_amt',
        'scroll_pending_amt',
        'scroll_amt',
        'scroll_scrip_generated_amt',
        'liability',
        'liability_amount_paid',
      ];
      const isMatch = (item, searchRecord) => {
        for (let key in searchRecord) {
          if (key === 'key' || ignoreKeys.includes(key)) continue;
          if (item.hasOwnProperty(key) && item[key] !== searchRecord[key]) {
            return false;
          }
        }
        return true;
      };
      exportData = exportData.filter((item) => isMatch(item, record));
      exportSummaryData = exportSummaryData.filter((item) => isMatch(item, record));
    } else if (filterRemark && typeof filterRemark === 'string') {
      exportData = exportData.filter((item) => item.rodtep_remark === filterRemark);
      exportSummaryData = exportSummaryData.filter((item) => item.rodtep_remark === filterRemark);
    }

    summaryWorksheet.columns = filteredSummaryColumns.map((col) => ({
      header: col.title,
      key: col.dataIndex,
      width: Math.floor((col.width || 150) / 7),
    }));
    summaryWorksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC000' } };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
    });
    exportSummaryData.forEach((obj) => {
      summaryWorksheet.addRow({
        business_unit: obj.business_unit || '',
        plant_name: obj.plant_name || '',
        fy: obj.fy || '',
        iec_br: obj.iec_br || '',
        port_code: obj.port_code || '',
        rodtep_remark: obj.rodtep_remark || obj.remark || '',
        rodtep_amt: obj.rodtep_amt ? parseFloat(obj.rodtep_amt) : 0,
        scroll_pending_amt: obj.scroll_pending_amt ? parseFloat(obj.scroll_pending_amt) : 0,
        scroll_amt: obj.scroll_amt ? parseFloat(obj.scroll_amt) : 0,
        scroll_scrip_generated_amt: obj.scroll_scrip_generated_amt ? parseFloat(obj.scroll_scrip_generated_amt) : 0,
        liability: obj.liability ? parseFloat(obj.liability) : 0,
        liability_amount_paid: obj.liability_amount_paid ? parseFloat(obj.liability_amount_paid) : 0,
      });
    });

    worksheet.columns = filteredColumns.map((col) => ({
      header: col.title,
      key: col.dataIndex,
      width: Math.max(15, Math.floor((col.width || 150) / 7)),
    }));
    worksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC000' } };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
    });
    exportData.forEach((obj) => {
      const formattedObj = {
        ...obj,
        remark: obj.remark || obj.liability_paid_remark || '',
        liability_paid_remark: obj.liability_paid_remark || obj.remark || '',
        rodtep_scrip_expiry_date: obj.rodtep_scrip_expiry_date ? formatDateToDDMMYY(obj.rodtep_scrip_expiry_date) : '',
        rodtep_scrip_issue_date: obj.rodtep_scrip_issue_date ? formatDateToDDMMYY(obj.rodtep_scrip_issue_date) : '',
        date_of_realization: obj.date_of_realization ? formatDateToDDMMYY(obj.date_of_realization) : '',
        rodtep_scroll_date: obj.rodtep_scroll_date ? formatDateToDDMMYY(obj.rodtep_scroll_date) : '',
        leo_date: obj.leo_date ? formatDateToDDMMYY(obj.leo_date) : '',
        sb_date: obj.sb_date ? formatDateToDDMMYY(obj.sb_date) : '',
        challan_date: obj.challan_date ? formatDateToDDMMYY(obj.challan_date) : '',
        egm_date: obj.egm_date ? formatDateToDDMMYY(obj.egm_date) : '',
        rodtep_amt: obj.rodtep_amt != null ? parseFloat(obj.rodtep_amt) : '',
        rodtep_scroll_amount: obj.rodtep_scroll_amount != null ? parseFloat(obj.rodtep_scroll_amount) : '',
        value_difference: obj.value_difference != null ? parseFloat(obj.value_difference) : '',
        liability: obj.liability != null ? parseFloat(obj.liability) : '',
        rodtep_scrip_sb_amount: obj.rodtep_scrip_sb_amount != null ? parseFloat(obj.rodtep_scrip_sb_amount) : '',
        challan_amount: obj.challan_amount != null ? parseFloat(obj.challan_amount) : '',
        liability_amount_paid: obj.liability_amount_paid != null ? parseFloat(obj.liability_amount_paid) : '',
      };
      worksheet.addRow(formattedObj);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const fileNameSuffix =
      filterRemark && typeof filterRemark === 'string' ? `_${filterRemark}` : '';
    saveAs(
      blob,
      `Lucrative's CIP DSS - RoDTEP Status as on ${getTodaysDate()} of ${selectedIEC}${fileNameSuffix}.xlsx`
    );
  };

  const filteredSummaryColumns = dashboardReportSummaryColumns.filter((col) => {
    const key = String(col.dataIndex || col.key);
    const isDefinedInDetail = columns.some((c) => String(c.dataIndex || c.key) === key);
    return isDefinedInDetail ? visibleKeys.includes(key) : true;
  });

  return (
    <Fragment>
      <style>{tableStyles}</style>
      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between">
              <div className="header-title">
                <h4 className="card-title">
                  Lucrative's CIP Report - RoDTEP Status as on {formattedDate} of {selectedIECName}
                </h4>
              </div>
              <div className="d-flex align-items-center gap-2">
                <ColumnSelector
                  allColumns={columns}
                  visibleKeys={visibleKeys}
                  setVisibleKeys={setVisibleKeys}
                  onReset={resetToDefault}
                />
                <button className="btn btn-primary cip-btn-submit" onClick={() => exportToExcel()}>
                  Export to Excel
                </button>
              </div>
            </Card.Header>

            <Card.Body className="d-flex justify-content-start align-items-center gap-2 ps-4">
              {(userRole === 'superadmin' || userRole === 'group_head') && (
                <Select
                  showSearch
                  placeholder="Select IEC"
                  onChange={handleIECSelection}
                  allowClear
                  options={IECOptions}
                  style={{ width: '300px' }}
                />
              )}
              <ReportDateFilter dates={dates} onChange={handleDateChange} />
              <Input
                placeholder="Enter Bill No"
                allowClear
                value={billNo}
                onChange={(e) => handleBillSearch(e.target.value)}
                style={{ width: 250 }}
              />
              <Select
                mode="multiple"
                allowClear
                style={{ width: 250 }}
                placeholder="Select Branch Code"
                onChange={handleBranchCodeSelection}
                options={branchCodeNames.map((item) => ({
                  value: item.branch_code,
                  label: `${item.branch_code} / ${item.plant_name} (${item.business_unit})`,
                }))}
              />
              <Button
                type="primary"
                className="cip-btn-submit"
                onClick={handleSubmit}
              >
                Submit
              </Button>
            </Card.Body>

            <Card.Body className="px-0 report-page">
              <Table
                className="rodtep-summary-table"
                columns={filteredSummaryColumns}
                dataSource={summaryDashboardData}
                pagination={false}
                loading={isLoading2}
                bordered
                scroll={{ x: 'max-content' }}

              />

              <hr />

              <Table
                className="rodtep-table"
                bordered
                columns={filteredColumns}
                dataSource={jsonData}
                loading={isLoading}
                scroll={{ x: 'max-content' }}

                pagination={{
                  showTotal: (total) => `Total ${total} records`,
                  showSizeChanger: true,
                }}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
};

export default RodtepReport;